import React from 'react';
import {
    Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, LinearProgress,
    TextField, Tooltip, Typography
} from '@mui/material';
import {styled} from '@mui/system';
import CountrySelect from '../utils/CountrySelect';
import UrlWithParams from '../input/UrlWithParams';
import {GetDefaultTrackingDomain} from '../../helpers/common';
import {postbackDomain} from '../../helpers/config';
import WorkspaceDropdown from '../workspace/WorkspaceDropdown';
import ReactHookTagsSelect from '../tags/ReactHookTagsSelect';
import CopyContentToClipboard from '../utils/CopyContentToClipboard';
import {LandingSection} from './LandingDesign';
import {SettingsDialogDescription} from '../settings/SettingsDialog';
import {Info} from "@mui/icons-material";

// Shared by campaign and offer forms as well as country selectors.
export const CustomTooltip = styled(Tooltip)({marginLeft: 6, fontSize: 18, position: 'relative', cursor: 'pointer'});

const fieldGrid = {display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 2, minWidth: 0};

export const LandingForm = ({
                                title, watch, control, register, handleClose, requestError, errors, handleSubmit, onSubmit,
                                loading, setValue, landingData, getValues, submitLabel = 'Save changes'
                            }) => {
    const formId = React.useId();
    const trackingDomain = GetDefaultTrackingDomain() || postbackDomain;
    const displayName = (watch('country')?.label || 'Global') + ' - ' + (watch('name') || 'Landing page name');

    return <>
        <DialogTitle id="landing-form-title">{title}
            <SettingsDialogDescription>Manage landing details, URL parameters and links to your
                offers.</SettingsDialogDescription>
        </DialogTitle>
        {loading && <LinearProgress aria-label="Loading landing" sx={{mx: {xs: 2, sm: 3}, borderRadius: 1}}/>}
        <DialogContent>
            {requestError && <Alert severity="error" sx={{mb: 2}}>{requestError}</Alert>}
            <Box component="form" id={formId} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <Box component="fieldset" disabled={loading}
                     sx={{border: 0, p: 0, m: 0, minWidth: 0, display: 'grid', gap: 2}}>
                    <LandingSection title="Landing details">
                        <Box sx={fieldGrid}>
                            <Box sx={{minWidth: 0}}><WorkspaceDropdown control={control}
                                                                       workspaceId={landingData?.workspaceId}
                                                                       setValue={setValue}
                                                                       validate={value => value?.value > 0 || 'Please select a workspace.'}/></Box>
                            <TextField {...register('name', {required: true, maxLength: 255, minLength: 3})}
                                       error={!!errors.name}
                                       helperText={errors.name ? 'Enter a name between 3 and 255 characters.' : undefined}
                                       label="Name *" placeholder="Landing page name" fullWidth size="small"
                                       slotProps={{inputLabel: {shrink: true}}}/>
                        </Box>

                        <Box display="flex" flexDirection="row" flexWrap="noWrap">
                            <Box flexGrow={1}>
                                <CountrySelect control={control} compact/>
                            </Box>
                            <Box sx={{pt: 1, pr: 1}}>
                                <Tooltip
                                    disableFocusListener
                                    sx={{
                                        marginLeft: 2,
                                        fontSize: "18px",
                                        position: "relative",
                                        cursor: "pointer"
                                    }}
                                    title="Country helps organize your landings and prefixes their names. It does not affect
                                    traffic distribution.">
                                    <Info color={"primary"} fontSize={"small"}/>
                                </Tooltip>
                            </Box>
                        </Box>

                        <Box sx={{minWidth: 0}}>
                            <Typography sx={{fontSize: 11, color: 'text.secondary', mb: 0.5}}>Display name</Typography>
                            <Typography
                                sx={{fontSize: 13, fontWeight: 500, overflowWrap: 'anywhere'}}>{displayName}</Typography>
                        </Box>

                        <Box sx={{
                            minWidth: 0, '& .MuiAutocomplete-root > .MuiBox-root': {alignItems: 'center'},
                            '& .MuiAutocomplete-root > .MuiBox-root > .MuiBox-root:first-of-type': {minWidth: 0},
                            '& .MuiAutocomplete-root > .MuiBox-root > .MuiBox-root:last-child': {py: 0, '& > div': {paddingTop: '0 !important'}}
                        }}>
                            <ReactHookTagsSelect control={control} inputName="tags" inputLabel="Tags" inputLabelShrink
                                                 getValues={getValues}/>
                        </Box>
                    </LandingSection>

                    <LandingSection title="Landing URL">
                        <UrlWithParams label="Landing URL *" name="url" register={register} errors={errors}
                                       watch={watch} setValue={setValue} compact/>
                    </LandingSection>

                    <LandingSection title="Offer links"
                                    description="Use these links for the call-to-action buttons on your landing page.">
                        <Box sx={{
                            minWidth: 0, display: 'grid', gap: 2, '& .MuiOutlinedInput-root': {fontFamily: 'monospace'},
                            '& .MuiBox-root': {minWidth: 0}, '& > div > .MuiBox-root': {alignItems: 'center'},
                            '& > div > .MuiBox-root > .MuiBox-root:last-child': {mt: 0}
                        }}>
                            <CopyContentToClipboard label="Random offer link"
                                                    copyText={`https://${trackingDomain}/click`}/>
                            <CopyContentToClipboard label="Specific offer link"
                                                    copyText={`https://${trackingDomain}/click/1`}/>
                        </Box>
                        <Typography sx={{fontSize: 12, color: 'text.secondary', lineHeight: 1.65}}>
                            The random link follows your campaign’s offer distribution. Replace the final number in the
                            specific link to select an offer by its position.
                        </Typography>
                    </LandingSection>
                </Box>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button type="button" color="inherit" disabled={loading} onClick={handleClose}>Cancel</Button>
            <Button type="submit" form={formId} variant="contained" disabled={loading}
                    startIcon={loading ? <CircularProgress size={16}
                                                           color="inherit"/> : undefined}>{loading ? 'Please wait…' : submitLabel}</Button>
        </DialogActions>
    </>;
};
